import { addExpense, firestoreState, updateExpense } from "../stores/firestoreStore"
import type { FirestoreExpense, RecurringFrequency } from "../types/firestore"
import { logger } from "../utils/logger"
import { toMonthly } from "../utils/monthlyIncome"
import type { DocUpdate } from "./databaseService"

/**
 * Parse a date-only string ("YYYY-MM-DD") as LOCAL midnight. `new Date(s)`
 * parses such strings as UTC midnight, which is the *previous* local day for
 * users east of Greenwich — recurring expenses ended up dated a day early and
 * nextOccurrence drifted backwards one day per cycle.
 */
export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}

/**
 * Format a Date as "YYYY-MM-DD" using local date parts. `toISOString()`
 * formats in UTC, shifting a local-midnight date back one day in UTC+
 * timezones.
 */
export function formatLocalDate(date: Date): string {
  const m = date.getMonth() + 1
  const d = date.getDate()
  return `${date.getFullYear()}-${m < 10 ? `0${m}` : m}-${d < 10 ? `0${d}` : d}`
}

/**
 * Calculate the next occurrence date based on frequency and current date
 */
export function calculateNextOccurrence(
  frequency: RecurringFrequency,
  fromDate: Date,
  dayOfMonth?: number,
  dayOfWeek?: number,
): Date {
  const next = new Date(fromDate)

  switch (frequency) {
    case "weekly":
      next.setDate(next.getDate() + 7)
      if (dayOfWeek !== undefined) {
        // Adjust to the specific day of week
        const currentDay = next.getDay()
        const diff = dayOfWeek - currentDay
        next.setDate(next.getDate() + (diff <= 0 ? diff + 7 : diff))
      }
      break

    case "bi-weekly":
      next.setDate(next.getDate() + 14)
      if (dayOfWeek !== undefined) {
        const currentDay = next.getDay()
        const diff = dayOfWeek - currentDay
        if (diff !== 0) {
          next.setDate(next.getDate() + diff)
        }
      }
      break

    case "monthly": {
      // Set day to 1 first to avoid overflow issues when changing months
      const targetMonth = next.getMonth() + 1
      const targetYear = next.getFullYear()
      next.setDate(1)
      next.setMonth(targetMonth)
      // Handle months with fewer days
      const maxDayMonthly = new Date(targetYear, targetMonth + 1, 0).getDate()
      const targetDayMonthly = dayOfMonth !== undefined ? dayOfMonth : fromDate.getDate()
      next.setDate(Math.min(targetDayMonthly, maxDayMonthly))
      break
    }

    case "quarterly": {
      const targetMonthQ = next.getMonth() + 3
      next.setDate(1)
      next.setMonth(targetMonthQ)
      const maxDayQuarterly = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()
      const targetDayQuarterly = dayOfMonth !== undefined ? dayOfMonth : fromDate.getDate()
      next.setDate(Math.min(targetDayQuarterly, maxDayQuarterly))
      break
    }

    case "annually": {
      const targetYearA = next.getFullYear() + 1
      next.setDate(1)
      next.setFullYear(targetYearA)
      const maxDayAnnually = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()
      const targetDayAnnually = dayOfMonth !== undefined ? dayOfMonth : fromDate.getDate()
      next.setDate(Math.min(targetDayAnnually, maxDayAnnually))
      break
    }
  }

  return next
}

/**
 * Calculate the initial next occurrence based on start date and frequency
 */
export function calculateInitialNextOccurrence(
  startDate: Date,
  frequency: RecurringFrequency,
  dayOfMonth?: number,
  dayOfWeek?: number,
): Date {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let next = new Date(startDate)
  next.setHours(0, 0, 0, 0)

  // If start date is in the future, use it
  if (next > today) {
    return next
  }

  // Otherwise, calculate the next occurrence from today
  next = new Date(today)

  // For weekly/bi-weekly, find the next occurrence of the specified day
  if ((frequency === "weekly" || frequency === "bi-weekly") && dayOfWeek !== undefined) {
    const currentDay = next.getDay()
    let diff = dayOfWeek - currentDay
    if (diff < 0) diff += 7
    if (diff === 0 && frequency === "bi-weekly") {
      // If today is the day, check if we should skip to next bi-weekly
      diff = 14
    }
    next.setDate(next.getDate() + diff)
    return next
  }

  // For monthly/quarterly/annually, find the next occurrence of the specified day of month
  if (dayOfMonth !== undefined) {
    const currentDate = next.getDate()
    if (currentDate > dayOfMonth) {
      // Move to next period
      if (frequency === "monthly") {
        next.setMonth(next.getMonth() + 1)
      } else if (frequency === "quarterly") {
        next.setMonth(next.getMonth() + 3)
      } else if (frequency === "annually") {
        next.setFullYear(next.getFullYear() + 1)
      }
    }
    const maxDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()
    next.setDate(Math.min(dayOfMonth, maxDay))
    return next
  }

  // Default: just add one period from today
  return calculateNextOccurrence(frequency, today, dayOfMonth, dayOfWeek)
}

/**
 * Get all recurring expense templates
 */
export function getRecurringTemplates(): FirestoreExpense[] {
  return firestoreState.expenses.value.filter((e: FirestoreExpense) => e.isRecurring && e.isActive !== false)
}

/**
 * Anchor day for monthly-style schedules: the explicit dayOfMonth when set,
 * otherwise the day of the template's own start date. Falling back to the
 * rolling previous occurrence (the old behavior) made a bill created on
 * Jan 31 drift to the 28th forever after crossing February.
 */
function templateAnchorDay(template: FirestoreExpense): number | undefined {
  if (template.dayOfMonth !== undefined) return template.dayOfMonth
  return template.date ? parseLocalDate(template.date).getDate() : undefined
}

/**
 * Get upcoming recurring expenses for the next N days
 */
export function getUpcomingRecurringExpenses(days: number = 30): Array<{ template: FirestoreExpense; dueDate: Date }> {
  const templates = getRecurringTemplates()
  const upcoming: Array<{ template: FirestoreExpense; dueDate: Date }> = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const endDate = new Date(today)
  endDate.setDate(endDate.getDate() + days)

  for (const template of templates) {
    // Missing frequency would make calculateNextOccurrence return the same
    // date forever and spin this loop — same guard as processRecurringExpenses.
    if (!template.nextOccurrence || !template.frequency) continue

    let dueDate = parseLocalDate(template.nextOccurrence)

    // Add all occurrences within the range
    while (dueDate <= endDate) {
      // Check if past end date
      if (template.endDate && dueDate > parseLocalDate(template.endDate)) {
        break
      }

      if (dueDate >= today) {
        upcoming.push({ template, dueDate: new Date(dueDate) })
      }

      // Calculate next occurrence; bail if it doesn't advance (unknown
      // frequency value) rather than loop forever.
      const next = calculateNextOccurrence(template.frequency, dueDate, templateAnchorDay(template), template.dayOfWeek)
      if (next.getTime() <= dueDate.getTime()) break
      dueDate = next
    }
  }

  // Sort by due date
  upcoming.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime())

  return upcoming
}

/**
 * Process all due recurring expenses and create actual expense entries
 * Returns the number of expenses created
 */
export async function processRecurringExpenses(): Promise<number> {
  const templates = getRecurringTemplates()
  const today = new Date()
  today.setHours(23, 59, 59, 999) // End of today

  let created = 0

  // Each template is processed independently: one failing template (bad data,
  // rejected write) must not stall every template after it — a permanent
  // failure would otherwise block recurring processing forever.
  for (const template of templates) {
    if (!template.nextOccurrence || !template.frequency) continue

    try {
      let nextOccurrence = parseLocalDate(template.nextOccurrence)

      const end = template.endDate ? parseLocalDate(template.endDate) : null

      // Process all due occurrences up to and including the end date — the
      // occurrence due ON the end date is still owed, so deactivation must
      // happen after generation, not before (deactivating first silently
      // dropped the final payment).
      while (nextOccurrence <= today && (!end || nextOccurrence <= end)) {
        try {
          // Create the actual expense entry
          await addExpense({
            description: template.description,
            amount: template.amount,
            category: template.category,
            account: template.account,
            ...(template.accountId ? { accountId: template.accountId } : {}),
            date: formatLocalDate(nextOccurrence),
            type: template.type,
            templateId: template.id, // Link back to template
            // Carry household split settings onto each generated occurrence so
            // recurring shared bills still feed into settle-up. Spread
            // conditionally — Firestore rejects undefined field values.
            ...(template.paidBy ? { paidBy: template.paidBy } : {}),
            ...(template.splitMode ? { splitMode: template.splitMode } : {}),
            ...(template.splitWith ? { splitWith: template.splitWith } : {}),
            ...(template.splitRatios ? { splitRatios: template.splitRatios } : {}),
            ...(template.splitAmounts ? { splitAmounts: template.splitAmounts } : {}),
            ...(template.splitOwner ? { splitOwner: template.splitOwner } : {}),
          })
          created++
        } catch (error) {
          // The occurrence already exists (double mount, other device): still
          // advance nextOccurrence below, otherwise the same collision recurs
          // on every mount and stalls processing permanently. Covers both the
          // local duplicate check and the server's unique index on
          // (template_id, date), which surfaces as a 409. Anything else
          // aborts this template only.
          const isLocalDuplicate = error instanceof Error && error.message === "DUPLICATE_TRANSACTION"
          const isServerDuplicate = (error as { status?: number })?.status === 409
          if (!isLocalDuplicate && !isServerDuplicate) {
            throw error
          }
        }

        // Calculate next occurrence
        const newNextOccurrence = calculateNextOccurrence(
          template.frequency,
          nextOccurrence,
          templateAnchorDay(template),
          template.dayOfWeek,
        )

        // Update template with new next occurrence
        await updateExpense(template.id, {
          nextOccurrence: formatLocalDate(newNextOccurrence),
        })

        // Update for next iteration
        nextOccurrence = newNextOccurrence
      }

      // Deactivate once the schedule has run past its end date.
      if (end && nextOccurrence > end && template.isActive !== false) {
        await updateExpense(template.id, { isActive: false })
      }
    } catch (error) {
      logger.error("Failed to process recurring template", error, { templateId: template.id })
    }
  }

  return created
}

/**
 * Create a recurring expense template
 */
export async function createRecurringExpense(
  expense: Omit<FirestoreExpense, "id" | "userId" | "createdAt" | "updatedAt">,
  frequency: RecurringFrequency,
  startDate: Date,
  options: {
    dayOfMonth?: number
    dayOfWeek?: number
    endDate?: Date
  } = {},
): Promise<string> {
  const nextOccurrence = calculateInitialNextOccurrence(startDate, frequency, options.dayOfMonth, options.dayOfWeek)

  return addExpense({
    ...expense,
    isRecurring: true,
    frequency,
    dayOfMonth: options.dayOfMonth,
    dayOfWeek: options.dayOfWeek,
    nextOccurrence: formatLocalDate(nextOccurrence),
    endDate: options.endDate ? formatLocalDate(options.endDate) : undefined,
    isActive: true,
  })
}

/**
 * Update a recurring expense template. Recomputes `nextOccurrence` from the
 * template's start date and (possibly edited) schedule so a change to the
 * frequency or day is reflected in when the next occurrence fires. `updates`
 * carries the plain fields (amount, description, category, account, …); the
 * schedule fields are passed explicitly so callers can't half-update them.
 */
export async function updateRecurringExpense(
  templateId: string,
  updates: DocUpdate<FirestoreExpense>,
  frequency: RecurringFrequency,
  startDate: Date,
  options: {
    dayOfMonth?: number
    dayOfWeek?: number
    endDate?: Date
  } = {},
): Promise<void> {
  const nextOccurrence = calculateInitialNextOccurrence(startDate, frequency, options.dayOfMonth, options.dayOfWeek)

  await updateExpense(templateId, {
    ...updates,
    isRecurring: true,
    frequency,
    dayOfMonth: options.dayOfMonth,
    dayOfWeek: options.dayOfWeek,
    nextOccurrence: formatLocalDate(nextOccurrence),
    // null clears a removed end date server-side; undefined would be dropped
    // from the PATCH JSON and the bill would still stop at the old end date.
    endDate: options.endDate ? formatLocalDate(options.endDate) : null,
  })
}

/**
 * Pause a recurring expense
 */
export async function pauseRecurringExpense(templateId: string): Promise<void> {
  await updateExpense(templateId, { isActive: false })
}

/**
 * Resume a paused recurring expense
 */
export async function resumeRecurringExpense(templateId: string): Promise<void> {
  const template = firestoreState.expenses.value.find((e: FirestoreExpense) => e.id === templateId)
  if (!template || !template.isRecurring) return

  // Recalculate next occurrence from today
  const today = new Date()
  const nextOccurrence = calculateInitialNextOccurrence(
    today,
    template.frequency!,
    template.dayOfMonth,
    template.dayOfWeek,
  )

  await updateExpense(templateId, {
    isActive: true,
    nextOccurrence: formatLocalDate(nextOccurrence),
  })
}

/**
 * Convert an amount at the given frequency to its monthly equivalent.
 * Delegates to the single conversion table in utils/monthlyIncome so the
 * 4.33-weeks-per-month style factors can never drift between features.
 */
export function convertToMonthlyAmount(amount: number, frequency: RecurringFrequency): number {
  return toMonthly(amount, frequency)
}

/**
 * Get human-readable frequency label
 */
export function getFrequencyLabel(frequency: RecurringFrequency): string {
  const labels: Record<RecurringFrequency, string> = {
    weekly: "Weekly",
    "bi-weekly": "Every 2 weeks",
    monthly: "Monthly",
    quarterly: "Quarterly",
    annually: "Annually",
  }
  return labels[frequency]
}

/**
 * Get day of week label
 */
export function getDayOfWeekLabel(dayOfWeek: number): string {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
  return days[dayOfWeek] || ""
}
